import type {
  DrillDownCardElement,
  QueueCardElement,
  TextBlockElement,
} from '../../../demo-elements/types'
import type { DemoChatScript } from '../../../types'

const NETWORK_GRID: QueueCardElement = {
  kind: 'queue-card',
  id: 'labor-network-grid',
  title: 'Cascade 3PL — Network Labor Forecast — Mon May 25 – Fri May 29',
  kpis: [
    {
      label: 'Network orders',
      value: '134,200',
      sub: '4 facilities · 5 days',
      tone: 'neutral',
    },
    {
      label: 'Labor needed',
      value: '5,378 hrs',
      sub: 'pick/pack network-wide',
      tone: 'neutral',
    },
    {
      label: 'FT capacity',
      value: '5,320 hrs',
      sub: '133 picker FTE × 5 × 8',
      tone: 'neutral',
    },
    {
      label: 'Net gap',
      value: '+58 hrs short',
      sub: '2 facilities short · 2 long · geography-bound',
      tone: 'warning',
    },
  ],
  columns: [
    { id: 'facility', label: 'Facility' },
    { id: 'orders', label: 'Orders', align: 'right', compact: true },
    { id: 'need', label: 'Need', align: 'right', compact: true },
    { id: 'cap', label: 'FT cap', align: 'right', compact: true },
    { id: 'gap', label: 'Gap', align: 'right', compact: true },
    { id: 'rec', label: 'Recommendation' },
  ],
  rows: [
    {
      id: 'facility-atl',
      status: 'critical',
      cells: {
        facility: 'Atlanta',
        orders: '38,200',
        need: '1,530h',
        cap: '1,400h',
        gap: { value: '+130h short', tone: 'critical' },
        rec: '20 Traba slots Mon+Tue',
      },
    },
    {
      id: 'facility-indy',
      status: 'critical',
      emphasize: true,
      cells: {
        facility: 'Indianapolis HQ',
        orders: '61,200',
        need: '2,448h',
        cap: '2,400h',
        gap: { value: '+48h short', tone: 'critical' },
        rec: '38 Traba slots Mon–Wed',
      },
    },
    {
      id: 'facility-clt',
      status: 'warning',
      cells: {
        facility: 'Charlotte',
        orders: '17,200',
        need: '680h',
        cap: '720h',
        gap: { value: '−40h long', tone: 'warning' },
        rec: 'Release 5 FT or batch ahead',
      },
    },
    {
      id: 'facility-rno',
      status: 'warning',
      cells: {
        facility: 'Reno',
        orders: '17,600',
        need: '720h',
        cap: '800h',
        gap: { value: '−80h long', tone: 'warning' },
        rec: 'Release 10 FT or batch ahead',
      },
    },
  ],
  footer:
    'Geography pins demand: east/midwest brands ship from Indy + Atlanta; west-coast from Reno; southeast overflow at Charlotte. No cross-facility rebalance possible. Network Traba recommendation: 58 slots in 2 markets (Indy 38 + Atlanta 20) ≈ $7,500 contingent labor.',
}

const INDY_DAILY_GRID: QueueCardElement = {
  kind: 'queue-card',
  id: 'labor-indy-grid',
  title: 'Indianapolis HQ · 5-day breakdown',
  kpis: [],
  columns: [
    { id: 'day', label: 'Day' },
    { id: 'orders', label: 'Orders', align: 'right', compact: true },
    { id: 'range', label: '80% band', align: 'right', compact: true },
    { id: 'need', label: 'Need', align: 'right', compact: true },
    { id: 'gap', label: 'Gap', align: 'right', compact: true },
    { id: 'rec', label: 'Recommendation' },
  ],
  rows: [
    {
      id: 'labor-indy-mon',
      status: 'critical',
      cells: {
        day: 'Mon May 25',
        orders: '14,200',
        range: '12,800–15,600',
        need: '568h',
        gap: { value: '+88h short', tone: 'critical' },
        rec: '15 Traba slots, 8a–2p',
      },
    },
    {
      id: 'labor-indy-tue',
      status: 'critical',
      emphasize: true,
      cells: {
        day: 'Tue May 26',
        orders: '14,800',
        range: '13,400–16,200',
        need: '592h',
        gap: { value: '+112h short', tone: 'critical' },
        rec: '19 Traba slots, 8a–2p',
      },
    },
    {
      id: 'labor-indy-wed',
      status: 'warning',
      cells: {
        day: 'Wed May 27',
        orders: '12,500',
        range: '11,200–13,800',
        need: '500h',
        gap: { value: '+20h short', tone: 'warning' },
        rec: '4 Traba slots, 8a–2p',
      },
    },
    {
      id: 'labor-indy-thu',
      status: 'warning',
      cells: {
        day: 'Thu May 28',
        orders: '10,500',
        range: '9,400–11,700',
        need: '420h',
        gap: { value: '−60h long', tone: 'warning' },
        rec: 'Release 8 FT early',
      },
    },
    {
      id: 'labor-indy-fri',
      status: 'warning',
      cells: {
        day: 'Fri May 29',
        orders: '9,200',
        range: '8,300–10,200',
        need: '368h',
        gap: { value: '−112h long', tone: 'warning' },
        rec: 'Release 14 FT early',
      },
    },
  ],
  footer:
    'Indy Mon–Wed: 38 Traba picker/packer slots (15+19+4, 8a–2p, $22/hr).',
}

const TUESDAY_BRANDS: QueueCardElement = {
  kind: 'queue-card',
  id: 'labor-tue-brands',
  title: 'Indy Tuesday brand breakdown · top 5 by labor',
  kpis: [],
  columns: [
    { id: 'brand', label: 'Brand' },
    { id: 'orders', label: 'Orders', align: 'right', compact: true },
    { id: 'hrs', label: 'Labor hrs', align: 'right', compact: true },
    { id: 'note', label: 'Why' },
  ],
  rows: [
    {
      id: 'tue-trailmix',
      status: 'critical',
      cells: {
        brand: 'Trailmix Provisions',
        orders: '3,200',
        hrs: '128',
        note: 'Tuesday is consistently heavy',
      },
    },
    {
      id: 'tue-briar',
      status: 'critical',
      cells: {
        brand: 'Briar Coffee Roasters',
        orders: '2,800',
        hrs: '112',
        note: 'Monday roast spillover',
      },
    },
    {
      id: 'tue-northwind',
      status: 'critical',
      cells: {
        brand: 'Northwind Apparel',
        orders: '2,400',
        hrs: '116',
        note: 'West-coast surge · bulk-pickable',
      },
    },
    {
      id: 'tue-oakhill',
      status: 'good',
      cells: {
        brand: 'OakHill Skincare',
        orders: '1,200',
        hrs: '60',
        note: 'Multi-pick avg, slower per order',
      },
    },
    {
      id: 'tue-altitude',
      status: 'good',
      cells: {
        brand: 'Altitude Supplements',
        orders: '1,400',
        hrs: '56',
        note: 'On pace',
      },
    },
  ],
  footer: 'Plus 63 lower-volume brands totaling ~3,800 orders, ~120 hrs.',
}

const INDY_REASONING: TextBlockElement = {
  kind: 'text-block',
  id: 'labor-indy-reasoning',
  text: `**What Tuesday's 14,800 Indy forecast is leaning on:**
- Trailing 4-week velocity by brand, weighted toward recent weeks
- Day-of-week seasonality (Tuesdays run +8% vs weekly average)
- Current ShipHero open-order backlog at Indy
- Briar Coffee's Monday roast → Tuesday-ship spillover pattern
- Northwind's west-coast surge: +6% on NWA volume next week, concentrated Mon–Wed

_Model has been within 8% of actuals on 9 of the last 10 Tuesdays at Indy._`,
}

const SCENARIO_RESULT: DrillDownCardElement = {
  kind: 'drill-down-card',
  id: 'labor-scenario-mon',
  title: 'Scenario: 5 FT pickers no-show Monday at Indy',
  subtitle:
    'Recomputed Indy Mon May 25 only — other facilities + days unchanged',
  facts: [
    { label: 'Indy FT capacity Mon', value: '480h → 440h', tone: 'critical' },
    { label: 'Indy gap Mon', value: '+88h → +128h', tone: 'critical' },
    {
      label: 'Indy recommended',
      value: '22 Traba slots Mon (was 15)',
      tone: 'warning',
    },
    { label: 'Cost delta', value: '+$924 vs baseline' },
  ],
}

const SCENARIO_RISK: TextBlockElement = {
  kind: 'text-block',
  id: 'labor-scenario-risk',
  text: `**Risk callout:**
Briar Coffee's 2,800 Tuesday-shipped orders depend on Monday's Indy batching being current. If Monday Indy slots can't fill by 9am, the safer path is pushing Briar's 8am Atlanta bulk-ship batch to 11am so Indy labor concentrates on the inbound roast first.

_Cost delta is $924. Risk is Briar's Tuesday SLA._`,
}

const TRABA_POST_CARD: DrillDownCardElement = {
  kind: 'drill-down-card',
  id: 'labor-traba-post',
  title: 'Traba slots — 2 markets · 58 picker/packer shifts',
  subtitle:
    'Indianapolis + Atlanta · pre-filled from last 6 posts at each facility',
  facts: [
    { label: 'Indy: Mon May 25', value: '15 slots · 8a–2p · $22/hr' },
    { label: 'Indy: Tue May 26', value: '19 slots · 8a–2p · $22/hr' },
    { label: 'Indy: Wed May 27', value: '4 slots · 8a–2p · $22/hr' },
    { label: 'Atlanta: Mon May 25', value: '12 slots · 8a–2p · $21/hr' },
    { label: 'Atlanta: Tue May 26', value: '8 slots · 8a–2p · $21/hr' },
    { label: 'Total contingent labor', value: '~$7,500 across both markets' },
    {
      label: 'Lead worker pref',
      value: 'Inherited per facility (last 6 posts)',
    },
  ],
  primaryAction: {
    label: 'Post 58 Traba slots across Indy + Atlanta',
    context: 'two markets · 8a–2p · standard pay + parking',
    preview:
      'Cascade 3PL — Picker/Packer · Indianapolis HQ: 15 Mon + 19 Tue + 4 Wed (8a–2p, $22/hr). Atlanta: 12 Mon + 8 Tue (8a–2p, $21/hr). Standard parking + lead-worker preference inherited per facility. Slack #ops-leads gets a fill notification per slot.',
    accent: 'order',
  },
}

const BEAT1_CLOSER: TextBlockElement = {
  kind: 'text-block',
  id: 'labor-beat1-closer',
  text: 'Indy and Atlanta short, Reno and Charlotte long, but geography pins demand. **Drill into Indianapolis — the biggest absolute shortage.**',
}

const BEAT2_CLOSER: TextBlockElement = {
  kind: 'text-block',
  id: 'labor-beat2-closer',
  text: 'Tuesday Indy is the heaviest single day in the network. Model accuracy holds against the last 10 Tuesdays. **Run a scenario — what if 5 FT no-show Monday at Indy?**',
}

const BEAT3_CLOSER: TextBlockElement = {
  kind: 'text-block',
  id: 'labor-beat3-closer',
  text: 'Same forecast, different inputs · cost of the change sized. **Post the 58 Traba slots across Indy + Atlanta from here?**',
}

const BEAT4_WRAP_CUE: TextBlockElement = {
  kind: 'text-block',
  id: 'labor-wrap-cue',
  text: "**Want to see how next week's margin shapes up too?**",
}

/**
 * Cascade 3PL — Demo 5: Labor Forecast (network-wide).
 *
 * Beat 1: Network 5-day forecast across all 4 facilities (Indy, Atlanta,
 * Reno, Charlotte). Indy + Atlanta short, Reno + Charlotte long; geography
 * pins demand so no cross-facility rebalance. Network gap +58 hrs net short.
 *
 * Beat 2: Drill into Indianapolis (largest absolute shortage). 5-day Indy
 * grid + Tuesday brand breakdown + reasoning.
 *
 * Beat 3: Scenario — 5 FT no-show Monday at Indy. Cost delta + Briar SLA
 * risk callout.
 *
 * Beat 4: Post 58 Traba picker/packer slots across two markets — Indy (38)
 * + Atlanta (20). ~$7,500 contingent labor cost. Lead-worker pref and
 * parking inherited per facility.
 */
export const laborForecast: DemoChatScript = {
  id: 'labor-forecast',
  name: 'Labor Forecast',
  description:
    "Demo 5. Five-day-ahead labor forecast across all 4 Cascade facilities — what next week's contingent labor need looks like, facility by facility, day by day.",
  triggers: [
    'labor',
    'labor forecast',
    'staffing',
    'staffing forecast',
    'traba',
    'traba slots',
    'next week labor',
    "next week's labor",
    'next week staffing',
    'shift planning',
    'shift plan',
    "tuesday's labor",
    'tuesday labor',
    'no-show',
    'no show',
    'monday no show',
    'pick pack',
    'pickers',
    'show me labor',
    'plan labor',
    'network labor',
    'facility labor',
  ],
  followUps: [
    {
      triggers: [
        'yes',
        'yeah',
        'yep',
        'sure',
        'ok',
        'okay',
        'go',
        'go ahead',
        'do it',
        'please',
        'continue',
        'next',
        "what's next",
        'whats next',
        'show me',
        'show me more',
        'more',
        'keep going',
        'margin',
        'p&l',
        'pnl',
        'next week margin',
      ],
      nextScriptId: 'customer-pnl-forecast',
    },
    {
      triggers: ['claims', 'carrier claims'],
      nextScriptId: 'carrier-claims-agent',
    },
    {
      triggers: ['post-shipment', 'post shipment', 'delays'],
      nextScriptId: 'post-shipment-support',
    },
  ],
  events: [
    // ─────────── Beat 1: Network 5-day forecast ───────────
    {
      type: 'status',
      delayMs: 200,
      message:
        'Pulling order forecasts + FT schedules across all 4 facilities...',
    },
    { type: 'thinking-start', delayMs: 200 },
    {
      type: 'thinking-delta',
      delayMs: 400,
      delta:
        '134,200 orders forecast network-wide for May 25–29. Splitting by facility based on routing rules + brand-facility assignments. Layering FT schedules from Manhattan SCALE.\n',
    },
    { type: 'thinking-stop', delayMs: 200 },
    {
      type: 'tool-call',
      delayMs: 200,
      name: 'forecast_next_week_labor_network',
    },
    {
      type: 'text-delta',
      delayMs: 500,
      delta: '+58 hours net short network-wide — 2 facilities short, 2 long.',
    },
    { type: 'demo-element-add', delayMs: 600, element: NETWORK_GRID },
    { type: 'demo-element-add', delayMs: 700, element: BEAT1_CLOSER },
    { type: 'done', delayMs: 400, title: 'Labor forecast — network May 25–29' },
    { type: 'await-user-prompt', delayMs: 0 },

    // ─────────── Beat 2: Indianapolis drill ───────────
    {
      type: 'status',
      delayMs: 200,
      message: 'Pulling Indianapolis 5-day breakdown + Tuesday brand mix...',
    },
    { type: 'tool-call', delayMs: 300, name: 'fetch_facility_breakdown' },
    {
      type: 'text-delta',
      delayMs: 500,
      delta: 'Indy: +220h short Mon–Wed, −172h long Thu+Fri.',
    },
    { type: 'demo-element-add', delayMs: 600, element: INDY_DAILY_GRID },
    { type: 'demo-element-add', delayMs: 700, element: TUESDAY_BRANDS },
    { type: 'demo-element-add', delayMs: 700, element: INDY_REASONING },
    { type: 'demo-element-add', delayMs: 700, element: BEAT2_CLOSER },
    {
      type: 'done',
      delayMs: 400,
      title: 'Indianapolis — 5-day + Tuesday drill',
    },
    { type: 'await-user-prompt', delayMs: 0 },

    // ─────────── Beat 3: scenario (5 FT no-show Mon at Indy) ───────────
    {
      type: 'status',
      delayMs: 200,
      message: 'Running scenario: 5 FT pickers no-show Monday at Indy...',
    },
    { type: 'tool-call', delayMs: 300, name: 'run_labor_scenario' },
    {
      type: 'text-delta',
      delayMs: 500,
      delta:
        'Indy Mon capacity 480h → 440h. Gap widens to +128h. 22 Traba slots needed Mon (was 15). +$924 cost delta.',
    },
    { type: 'demo-element-add', delayMs: 600, element: SCENARIO_RESULT },
    { type: 'demo-element-add', delayMs: 700, element: SCENARIO_RISK },
    { type: 'demo-element-add', delayMs: 700, element: BEAT3_CLOSER },
    { type: 'done', delayMs: 400, title: 'Scenario — Indy Mon recomputed' },
    { type: 'await-user-prompt', delayMs: 0 },

    // ─────────── Beat 4: Post Traba slots across 2 markets ───────────
    {
      type: 'status',
      delayMs: 200,
      message: 'Pre-filling 58 Traba shift posts across Indy + Atlanta...',
    },
    { type: 'tool-call', delayMs: 300, name: 'prepare_traba_shifts' },
    {
      type: 'text-delta',
      delayMs: 500,
      delta:
        'Role, market rate, parking, lead-worker preference all inherited from the last 6 posts at each facility.',
    },
    { type: 'demo-element-add', delayMs: 600, element: TRABA_POST_CARD },
    {
      type: 'approval',
      delayMs: 700,
      proposal: {
        proposalId: 'demo-cascade-labor-post-slots',
        toolName: 'post_traba_shifts',
        actionType: 'shift_post',
        description:
          'Post 58 Traba picker/packer slots across two markets. Indianapolis: 15 on Mon May 25 + 19 on Tue May 26 + 4 on Wed May 27 (8a-2p, $22/hr standard). Atlanta: 12 on Mon May 25 + 8 on Tue May 26 (8a-2p, $21/hr standard). Lead-worker preference inherited per facility. Parking instructions per facility. Slack #ops-leads gets a fill notification per slot. Approx contingent labor cost: $7,500.',
      },
    },
    { type: 'done', delayMs: 400, title: 'Traba slots — ready to post' },
    {
      type: 'await-approval-resolved',
      delayMs: 0,
      proposalId: 'demo-cascade-labor-post-slots',
    },

    // ─────────── Wrap: cue toward P&L forecast demo ───────────
    {
      type: 'status',
      delayMs: 200,
      message: 'Traba slots posted across both markets.',
    },
    {
      type: 'text-delta',
      delayMs: 400,
      delta:
        '58 slots scheduled. Notification when each fills. Reno and Charlotte rebalances logged for the Friday floor-lead Slack.',
    },
    { type: 'demo-element-add', delayMs: 500, element: BEAT4_WRAP_CUE },
    { type: 'done', delayMs: 400, title: 'Labor forecast demo — complete' },
  ],
}
